import React, { useEffect, useMemo, useState } from 'react'
import {
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Theme,
    Tooltip,
    makeStyles,
    useTheme,
} from '@material-ui/core'

import CachedIcon from '@material-ui/icons/Cached'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import HotelIcon from '@material-ui/icons/Hotel'
import ListAltIcon from '@material-ui/icons/ListAlt'
import SearchIcon from '@material-ui/icons/Search'
import TuneIcon from '@material-ui/icons/Tune'

import { Container } from '../../../../components'
import {
    FinderSelection,
    useAptConfig,
    useLease,
    useUser,
} from '../../../../hooks'
import {
    Lease,
    LeaseStatus,
    LeaseStatusType,
    ModelMap,
    traverse,
    Unit,
} from '../../../../models'
import {
    axiosInstance,
    isAccountManager,
    toMMDDYYYY,
} from '../../../../helpers'
import { UnitWithFolder, UpdateLeaseRequest } from '../../../../store'

import { toast } from 'react-toastify'

import {
    AutoCompleteFilterValue,
    DateFilterState,
    IssueFilterValue,
    LeaseStatusFilterState,
    PetFilterValue,
    TenantInspectionStatusFilterState,
} from '../../../../hooks/useLeaseFilter'
import { VersionedTree } from '../../../../hooks/useInfrastructure/types'
import { PaginationController } from '../../../DamageHome/MoveInspectionContainer'
import { LeaseRow } from './LeaseRow'
import {
    AppFolioTenantDirectoryImportModal,
    BulkTransitionModal,
    CreateLeaseModal,
    LeaseDetailModal,
    LeaseTransferModal,
    TenantInspectionModal,
} from '../../Forms'
import { LeaseHomeChips } from './LeaseHomeChips'
import { typographyStyle } from '../../../../theme/typographyStyle'
import { LeaseBedBoard } from './LeaseBedBoard'
import {
    buildLeaseBoardUnits,
    buildLeaseBoardUnitsFromOccupiableAreas,
    OccupiableAreaRow,
} from './leaseBoard'

const LEASE_HOME_FOOTER_HEIGHT = 32

type LeaseViewMode = 'leases' | 'bed_spaces'

const useStyles = makeStyles((theme: Theme) => ({
    tableCard: {
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflow: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        scrollbarWidth: 'auto',
        '&::-webkit-scrollbar': {
            width: 14,
            height: 14,
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: '#9e9e9e',
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: '#eeeeee',
        },
    },
    tableHead: {
        '& .MuiTableCell-stickyHeader': {
            backgroundColor: theme.palette.grey[50],
        },
        '& .MuiTableCell-head': {
            fontSize: '0.6875rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
            backgroundColor: theme.palette.grey[50],
        },
    },
    viewToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    emptyCell: {
        color: theme.palette.text.secondary,
        padding: theme.spacing(3),
    },
}))

interface Props {
    getFilteredLeases: (leaseList: Lease[], unitMap: ModelMap<Unit>) => Lease[]
    statusFilter: LeaseStatusFilterState
    setStatusFilter: (filter: LeaseStatusFilterState) => void
    filterDrawerOpen: boolean
    setFilterDrawerOpen: (open: boolean) => void
    searchValue: string
    setSearchValue: (val: string) => void
    dateFilter: DateFilterState
    setDateFilter: (state: DateFilterState) => void
    locationFinderSelection: FinderSelection
    tree: VersionedTree
    unitCount: number
    leaseList: Lease[]
    updateLeaseState: (lease: Lease) => void
    transitionLease: (
        leaseId: number,
        toStatus: LeaseStatusType,
    ) => Promise<Lease>
    isLoading: boolean
    setLeaseList: (leases: Lease[]) => void
    hasIssueFilter: IssueFilterValue
    setHasIssue: (has: IssueFilterValue) => void
    tenantInspectionStatusFilter: TenantInspectionStatusFilterState
    setTenantInspectionStatusFilter: (
        status: TenantInspectionStatusFilterState,
    ) => void
    autoCompleteFilter: AutoCompleteFilterValue
    setAutoCompleteFilter: (val: AutoCompleteFilterValue) => void
    lastSyncDate: Date | null
    updateLease: (request: UpdateLeaseRequest) => Promise<Lease>
    petFilter: PetFilterValue
    setPetFilter: (val: PetFilterValue) => void
}

export const LeaseHomeLeasesTab = (props: Props) => {
    const {
        getFilteredLeases,
        statusFilter,
        filterDrawerOpen,
        setFilterDrawerOpen,
        setSearchValue,
        searchValue,
        dateFilter,
        setDateFilter,
        locationFinderSelection,
        setStatusFilter,
        tree,
        leaseList,
        transitionLease,
        isLoading,
        setLeaseList,
        hasIssueFilter,
        setHasIssue,
        tenantInspectionStatusFilter,
        setTenantInspectionStatusFilter,
        updateLeaseState,
        autoCompleteFilter,
        setAutoCompleteFilter,
        updateLease,
        petFilter,
        setPetFilter,
    } = props

    const { workspaceUser } = useUser()
    const classes = useStyles()

    const [viewMode, setViewMode] = useState<LeaseViewMode>('leases')
    const [transferBusy, setTransferBusy] = useState(false)

    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [appFolioImportOpen, setAppFolioImportOpen] = useState(false)

    // const [transferModalOpen, setTransferModalOpen] = useState(false)

    const [transferLeaseIdx, setTransferLeaseIdx] = useState(-1)

    const [tenantInspectModalOpen, setTenantInspectModalOpen] = useState(false)
    const [allowTransitions, setAllowTransitions] = useState(false)

    const [pageSize, setPageSize] = useState(25)
    const [page, setPage] = useState(0)
    const [bulkTransitionModalOpen, setBulkTransitionModalOpen] = useState(
        false,
    )

    const [unitMap, setUnitMap] = useState<ModelMap<Unit>>({})

    const {
        leaseList: stagedLeaseList,
        getLeaseList: getStagedLeaseList,
        isLoading: stagedLeaseLoading,
        setLeaseList: setStagedLeaseList,
    } = useLease()

    const transitionAndCheckStaged = (
        leaseId: number,
        toStatus: LeaseStatusType,
    ) => {
        transitionLease(leaseId, toStatus).then((lease) => {
            if (toStatus === LeaseStatus.CURRENT) {
                setStagedLeaseList([...stagedLeaseList, lease])
            }
        })
    }

    const {
        getAreaConfigMap,
        getUnitConfigMap,
        areaConfigList,
        unitConfigList,
    } = useAptConfig({
        areaConfigList: true,
        unitConfigList: true,
    })
    const areaConfigMap = getAreaConfigMap()
    const unitConfigMap = getUnitConfigMap()

    const [selectedLeases, setSelectedLeases] = useState<boolean[] | null>(null)

    const [leaseDetail, setLeaseDetail] = useState<Lease | null>(null)

    const filteredLeases = getFilteredLeases(leaseList ?? [], unitMap)

    const [
        unitOccupancyDetails,
        setUnitOccupancyDetails,
    ] = useState<UnitOccState>({
        occupied_unit_list: [],
        partial_unit_list: [],
        vacant_unit_list: [],
    })

    // Load the unit details
    useEffect(() => {
        axiosInstance.get('lease/home_detail/').then((r) => {
            setUnitOccupancyDetails(r.data)
        })
        getStagedLeaseList({
            params: {
                statuses: `${LeaseStatus.CURRENT},`,
                home: true,
                staged: true,
                is_child: false,
            },
        })
    }, [])

    // Build a unit map
    useEffect(() => {
        const newUnitMap: ModelMap<Unit> = {}

        traverse(tree.root, (folder) => {
            folder.units.forEach((u) => {
                newUnitMap[u.id] = u
            })
        })
        setUnitMap(newUnitMap)
    }, [tree.version])

    // When the filters change, make sure that the users selected page still exists
    // If it does not select the the last valid page
    useEffect(() => {
        if (page + 1 > numPages) {
            setPage(Math.max(0, numPages - 1))
        }
    }, [filteredLeases])

    const [occupiableAreas, setOccupiableAreas] = useState<
        OccupiableAreaRow[] | null
    >(null)

    useEffect(() => {
        axiosInstance
            .get<OccupiableAreaRow[]>('infrastructure/area/', {
                params: { occupiable: true },
            })
            .then((response) => {
                setOccupiableAreas(
                    Array.isArray(response.data) ? response.data : [],
                )
            })
            .catch(() => {
                setOccupiableAreas(null)
            })
    }, [])

    const boardUnits = useMemo(() => {
        if (occupiableAreas) {
            return buildLeaseBoardUnitsFromOccupiableAreas(
                occupiableAreas,
                tree,
                unitConfigMap,
            )
        }
        return buildLeaseBoardUnits(tree, areaConfigMap, unitConfigMap)
    }, [occupiableAreas, tree, areaConfigList, unitConfigList])

    const refreshOccupancyDetails = () => {
        axiosInstance.get('lease/home_detail/').then((r) => {
            setUnitOccupancyDetails(r.data)
        })
    }

    const applyTransferredLease = (sourceId: number, transferred: Lease) => {
        const source = (leaseList ?? []).find((lease) => lease.id === sourceId)
        const nextLease: Lease = {
            ...transferred,
            children:
                transferred.children ??
                (transferred.id === sourceId ? source?.children : undefined),
        }
        if (nextLease.id === sourceId) {
            updateLeaseState(nextLease)
        } else {
            const current = leaseList ?? []
            const index = current.findIndex((lease) => lease.id === sourceId)
            if (index === -1) {
                setLeaseList([...current, nextLease])
            } else {
                const next = [...current]
                next.splice(index, 1, nextLease)
                setLeaseList(next)
            }
        }
        refreshOccupancyDetails()
    }

    const transferLeaseToArea = async (lease: Lease, areaId: number) => {
        setTransferBusy(true)
        try {
            const res = await axiosInstance.post(
                `lease/${lease.id}/transfer/`,
                {
                    area: areaId,
                },
            )
            applyTransferredLease(lease.id, res.data)
            toast.success('Lease transferred')
        } catch (e: any) {
            toast.error(
                e?.response?.data?.message || 'Could not transfer this lease.',
            )
            throw e
        } finally {
            setTransferBusy(false)
        }
    }

    const numPages = Math.ceil((filteredLeases?.length ?? 0) / pageSize)

    const paginatedList = useMemo(() => {
        const start = page * pageSize
        const end = start + pageSize

        return filteredLeases?.slice(start, end)
    }, [page, filteredLeases])

    const transferLease = paginatedList[transferLeaseIdx]

    const isUserAccountManager = isAccountManager(workspaceUser)
    const theme = useTheme()

    const activeWorkspace = workspaceUser?.active_workspace
    const showAppFolioTenantImport =
        activeWorkspace != null && activeWorkspace.is_appfolio_property

    return (
        <Container
            style={{
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflow: 'hidden',
                height: '100%',
                width: '100%',
            }}
        >
            <Container
                style={{
                    flexDirection: 'column',
                    flexShrink: 0,
                    minWidth: 0,
                    width: '100%',
                    overflow: 'hidden',
                }}
            >
                <Header
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    onClickAddLease={() => setCreateModalOpen(true)}
                    onClickFilter={() => setFilterDrawerOpen(!filterDrawerOpen)}
                    onClickTenantInspectReady={() =>
                        setTenantInspectModalOpen(true)
                    }
                    dateFilter={dateFilter}
                    setDateFilter={setDateFilter}
                    locationFinderSelection={locationFinderSelection}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    stagedCount={stagedLeaseList.length}
                    stagedLoading={stagedLeaseLoading}
                    setBulkMode={setBulkTransitionModalOpen}
                    bulkMode={bulkTransitionModalOpen}
                    hasIssue={hasIssueFilter}
                    setHasIssue={setHasIssue}
                    tenantInspectionStatusFilter={tenantInspectionStatusFilter}
                    setTenantInspectionStatusFilter={
                        setTenantInspectionStatusFilter
                    }
                    autoCompleteFilter={autoCompleteFilter}
                    setAutoCompleteFilter={setAutoCompleteFilter}
                    lastSyncDate={props.lastSyncDate}
                    petFilter={petFilter}
                    setPetFilter={setPetFilter}
                    entrataIntegrated={Boolean(
                        activeWorkspace?.entrata_integrated,
                    )}
                    showAppFolioTenantImport={showAppFolioTenantImport}
                    onOpenAppFolioTenantImport={() =>
                        setAppFolioImportOpen(true)
                    }
                    viewMode={viewMode}
                    onViewMode={setViewMode}
                />

                <Container
                    style={{
                        gap: theme.spacing(1.5),
                        marginTop: theme.spacing(2),
                        marginBottom: theme.spacing(2),
                        flexWrap: 'wrap',
                    }}
                >
                    <StatTile value={boardUnits.length} label="Total Units" />
                    <StatTile
                        value={unitOccupancyDetails.occupied_unit_list.length}
                        label="Occupied Units"
                    />
                    <StatTile
                        value={unitOccupancyDetails.vacant_unit_list.length}
                        label="Vacant Units"
                    />
                    <StatTile
                        value={unitOccupancyDetails.partial_unit_list.length}
                        label="Partial Units"
                    />
                </Container>
            </Container>

            {isLoading ? (
                <Container
                    style={{
                        flex: 1,
                        minHeight: 0,
                        justifyContent: 'center',
                        alignItems: 'center',
                        minWidth: 0,
                    }}
                >
                    <CircularProgress size={200} />
                </Container>
            ) : viewMode === 'bed_spaces' ? (
                <LeaseBedBoard
                    units={boardUnits}
                    filteredLeases={filteredLeases ?? []}
                    allLeases={leaseList ?? []}
                    searchValue={searchValue}
                    locationUnits={locationFinderSelection.selection.unit}
                    busy={transferBusy}
                    onOpenLease={setLeaseDetail}
                    onTransfer={transferLeaseToArea}
                />
            ) : (
                <div className={classes.tableCard}>
                    <Table stickyHeader size="small" style={{ minWidth: 1280 }}>
                        <TableHead className={classes.tableHead}>
                            <TableRow>
                                <TableCell>Tenant</TableCell>
                                <TableCell />
                                <TableCell>Location</TableCell>
                                <TableCell>Unit</TableCell>
                                <TableCell>Start</TableCell>
                                <TableCell>Move-in</TableCell>
                                <TableCell>End</TableCell>
                                <TableCell>Move-out</TableCell>
                                <TableCell>Lease Status</TableCell>
                                <TableCell>Move In Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(paginatedList?.length ?? 0) === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={11}
                                        className={classes.emptyCell}
                                    >
                                        No leases match these filters.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedList?.map((lease, index) => {
                                    const paginatedIndex =
                                        index + page * pageSize

                                    const idxChecked =
                                        selectedLeases === null
                                            ? false
                                            : selectedLeases[paginatedIndex]

                                    return (
                                        <LeaseRow
                                            key={`LEASE_ROW_${lease.id}`}
                                            lease={lease}
                                            onClickTransfer={() =>
                                                setTransferLeaseIdx(index)
                                            }
                                            unitMap={unitMap}
                                            areaConfigMap={areaConfigMap}
                                            transitionLease={
                                                transitionAndCheckStaged
                                            }
                                            onClick={setLeaseDetail}
                                            allowTransitions={allowTransitions}
                                            isChecked={idxChecked}
                                            onCheck={() =>
                                                setSelectedLeases(
                                                    (oldValues) => {
                                                        if (oldValues) {
                                                            const newValues = [
                                                                ...oldValues,
                                                            ]
                                                            newValues[
                                                                paginatedIndex
                                                            ] = !idxChecked
                                                            return newValues
                                                        }
                                                        return oldValues
                                                    },
                                                )
                                            }
                                            isAccountManager={
                                                isUserAccountManager
                                            }
                                        />
                                    )
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}
            {viewMode === 'leases' && (
                <Container
                    style={{
                        height: LEASE_HOME_FOOTER_HEIGHT,
                        flexShrink: 0,
                        alignItems: 'center',
                        overflow: 'hidden',
                        minWidth: 0,
                        width: '100%',
                    }}
                >
                    <div style={{ flex: 1 }} />
                    <PaginationController
                        size="small"
                        page={page}
                        numPages={numPages}
                        listLength={filteredLeases.length ?? 0}
                        setPage={setPage}
                    />
                </Container>
            )}

            <CreateLeaseModal
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                unitMap={unitMap}
                areaConfigMap={areaConfigMap}
            />

            <TenantInspectionModal
                open={tenantInspectModalOpen}
                onClose={() => setTenantInspectModalOpen(false)}
                leaseList={stagedLeaseList}
                unitMap={unitMap}
                areaConfigMap={areaConfigMap}
                setLeaseList={setStagedLeaseList}
            />

            <BulkTransitionModal
                open={bulkTransitionModalOpen}
                onClose={() => setBulkTransitionModalOpen(false)}
                leaseList={filteredLeases}
                unitMap={unitMap}
                areaConfigMap={areaConfigMap}
                setLeaseList={setLeaseList}
            />

            <LeaseDetailModal
                lease={leaseDetail}
                onClose={() => setLeaseDetail(null)}
                unitMap={unitMap}
                areaConfigMap={areaConfigMap}
                leaseList={leaseList}
                setLeaseList={setLeaseList}
                setLeaseDetail={setLeaseDetail}
                updateLease={updateLease}
            />

            <LeaseTransferModal
                open={transferLease !== undefined}
                onClose={() => setTransferLeaseIdx(-1)}
                lease={transferLease}
                unitMap={unitMap}
                areaConfigMap={areaConfigMap}
                onTransferLease={(updated) => {
                    if (transferLease) {
                        applyTransferredLease(transferLease.id, updated)
                    }
                }}
            />

            <AppFolioTenantDirectoryImportModal
                open={appFolioImportOpen}
                onClose={() => setAppFolioImportOpen(false)}
            />
        </Container>
    )
}

const Header: React.FC<{
    searchValue: string
    setSearchValue: (newVal: string) => void
    onClickAddLease: () => void
    onClickTenantInspectReady: () => void
    onClickFilter: () => void
    dateFilter: DateFilterState
    setDateFilter: (df: DateFilterState) => void
    locationFinderSelection: FinderSelection
    statusFilter: LeaseStatusFilterState
    setStatusFilter: (sf: LeaseStatusFilterState) => void
    stagedLoading: boolean
    stagedCount: number
    setBulkMode: (bool: boolean) => void
    bulkMode: boolean
    hasIssue: IssueFilterValue
    setHasIssue: (hasIssue: IssueFilterValue) => void
    tenantInspectionStatusFilter: TenantInspectionStatusFilterState
    setTenantInspectionStatusFilter: (
        newFilter: TenantInspectionStatusFilterState,
    ) => void
    autoCompleteFilter: AutoCompleteFilterValue
    setAutoCompleteFilter: (val: AutoCompleteFilterValue) => void
    lastSyncDate: Date | null
    petFilter: PetFilterValue
    setPetFilter: (val: PetFilterValue) => void
    entrataIntegrated: boolean
    showAppFolioTenantImport: boolean
    onOpenAppFolioTenantImport: () => void
    viewMode: LeaseViewMode
    onViewMode: (mode: LeaseViewMode) => void
}> = ({
    onClickAddLease,
    onClickTenantInspectReady,
    onClickFilter,
    searchValue,
    setSearchValue,
    stagedCount,
    stagedLoading,
    bulkMode,
    setBulkMode,
    dateFilter,
    setDateFilter,
    locationFinderSelection,
    statusFilter,
    setStatusFilter,
    hasIssue,
    setHasIssue,
    tenantInspectionStatusFilter,
    setTenantInspectionStatusFilter,
    autoCompleteFilter,
    setAutoCompleteFilter,
    lastSyncDate,
    petFilter,
    setPetFilter,
    entrataIntegrated,
    showAppFolioTenantImport,
    onOpenAppFolioTenantImport,
    viewMode,
    onViewMode,
}) => {
    const theme = useTheme()
    const classes = useStyles()
    return (
        <Container
            style={{
                flexDirection: 'column',
                flexShrink: 0,
                minWidth: 0,
                width: '100%',
                overflow: 'hidden',
            }}
        >
            <Container
                style={{
                    alignItems: 'center',
                    width: '100%',
                    minWidth: 0,
                    flexShrink: 0,
                }}
            >
                <span
                    style={{
                        fontStyle: 'normal',
                        fontWeight: 700,
                        fontSize: '28px',
                        lineHeight: '32px',
                    }}
                >
                    Lease Home
                </span>
                <div style={{ flex: 1 }} />

                {(entrataIntegrated || showAppFolioTenantImport) && (
                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            fontWeight: 700,
                            marginRight: theme.spacing(2),
                        }}
                    >
                        Last Sync:{' '}
                        {lastSyncDate ? toMMDDYYYY(lastSyncDate) : 'Never'}
                    </span>
                )}
                {entrataIntegrated && (
                    <Button
                        variant="outlined"
                        style={{
                            backgroundColor: '#fff',
                            color: '#008C85',
                            borderRadius: '196px',
                            border: '1px solid #008C85',
                            textTransform: 'none',
                        }}
                        onClick={() => {
                            axiosInstance
                                .get(`lease/entrata_sync/`)
                                .then((res) => {
                                    toast.success(res.data)
                                })
                        }}
                    >
                        <CachedIcon style={{ marginRight: 4 }} /> Sync
                    </Button>
                )}

                {showAppFolioTenantImport && (
                    <Tooltip title="Import AppFolio tenant directory (.xlsx)">
                        <IconButton
                            onClick={onOpenAppFolioTenantImport}
                            style={{
                                marginLeft: entrataIntegrated
                                    ? theme.spacing(1)
                                    : 0,
                                color: '#008C85',
                                border: '1px solid #008C85',
                                borderRadius: '196px',
                                padding: 8,
                            }}
                            aria-label="Import AppFolio tenant directory"
                        >
                            <CloudUploadIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Container>

            <div className={classes.viewToggle}>
                <Chip
                    icon={<ListAltIcon style={{ fontSize: 16 }} />}
                    label="Leases"
                    size="small"
                    clickable
                    color={viewMode === 'leases' ? 'primary' : 'default'}
                    onClick={() => onViewMode('leases')}
                    style={{ fontWeight: 600 }}
                />
                <Chip
                    icon={<HotelIcon style={{ fontSize: 16 }} />}
                    label="Bed Spaces"
                    size="small"
                    clickable
                    color={viewMode === 'bed_spaces' ? 'primary' : 'default'}
                    onClick={() => onViewMode('bed_spaces')}
                    style={{ fontWeight: 600 }}
                />
            </div>

            <LeaseHomeChips
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                locationFinderSelection={locationFinderSelection}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                hasIssue={hasIssue}
                setHasIssue={setHasIssue}
                issueWorkorderFilter={0}
                setIssueWorkorderFilter={() => {}}
                tenantInspectionStatusFilter={tenantInspectionStatusFilter}
                setTenantInspectionStatusFilter={
                    setTenantInspectionStatusFilter
                }
                autoCompleteFilter={autoCompleteFilter}
                setAutoCompleteFilter={setAutoCompleteFilter}
                petFilter={petFilter}
                setPetFilter={setPetFilter}
            />

            <Container
                style={{
                    alignItems: 'center',
                    width: '100%',
                    flexShrink: 0,
                }}
            >
                <TextField
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    variant="outlined"
                    placeholder="Search"
                    size="small"
                    InputProps={{
                        startAdornment: <SearchIcon htmlColor="#C2C2C2" />,
                    }}
                    style={{
                        borderRadius: '5px',
                        width: '224px',
                    }}
                />

                <Button
                    style={{
                        color: '#fff',
                        backgroundColor: '#008C85',
                        height: '33px',
                        width: '106px',
                        borderRadius: '5px',
                        borderWidth: 0,
                        marginLeft: '9px',
                        fontSize: '14px',
                        lineHeight: '17px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        textTransform: 'none',
                    }}
                    onClick={onClickAddLease}
                >
                    + Add Lease
                </Button>

                <div style={{ flex: 1 }} />
                <Button
                    variant="outlined"
                    style={{
                        color: '#fff',
                        backgroundColor: '#008C85',
                        height: '33px',
                        width: '106px',
                        borderRadius: '5px',
                        borderWidth: 0,
                        marginLeft: '9px',
                        fontSize: '14px',
                        lineHeight: '17px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        textTransform: 'none',
                    }}
                    onClick={() => {
                        setBulkMode(true)
                    }}
                >
                    Transition
                </Button>
                <Button
                    style={{
                        color: '#fff',
                        backgroundColor: '#008C85',
                        height: '33px',
                        width: '106px',
                        borderRadius: '5px',
                        borderWidth: 0,
                        marginLeft: '9px',
                        fontSize: '14px',
                        lineHeight: '17px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        textTransform: 'none',
                    }}
                    onClick={onClickTenantInspectReady}
                >
                    Staged {stagedLoading ? 'Loading' : `(${stagedCount})`}
                </Button>

                <IconButton onClick={onClickFilter}>
                    <TuneIcon htmlColor="#8A8A8A" />
                    <span
                        style={{
                            marginLeft: 8,
                            fontStretch: 'normal',
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '16px',
                            color: '#8A8A8A',
                        }}
                    >
                        Filter
                    </span>
                </IconButton>
            </Container>
        </Container>
    )
}

const StatTile: React.FC<{
    value: number
    label: string
}> = ({ value, label }) => {
    const theme = useTheme()
    return (
        <Paper
            elevation={0}
            style={{
                flex: 1,
                minWidth: 140,
                padding: theme.spacing(1.5, 2),
                borderRadius: 12,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: 72,
            }}
        >
            <span
                style={{
                    ...typographyStyle(theme.typography.h6),
                    fontWeight: 700,
                    lineHeight: 1.1,
                }}
            >
                {value}
            </span>
            <span
                style={{
                    ...typographyStyle(theme.typography.caption),
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    marginTop: 4,
                }}
            >
                {label}
            </span>
        </Paper>
    )
}

interface UnitOccState {
    occupied_unit_list: UnitWithFolder[]
    partial_unit_list: UnitWithFolder[]
    vacant_unit_list: UnitWithFolder[]
}
